import type { buildCheckoutSavedAddresses } from '../../../checkout/addressBookAdapter'
import type { useCheckoutFlow } from '../../../checkout/flow'
import {
  BillingAddressStep,
  CartReviewStep,
  RedirectingToPaymentStep,
  ReviewStep,
  ShippingAddressStep,
  ShippingMethodStep,
} from '../../../checkout/steps'
import type { ShopMessageKey } from '../../../i18n'

type CheckoutViewModel = ReturnType<typeof useCheckoutFlow>['viewModel']
type CheckoutSavedAddress = ReturnType<typeof buildCheckoutSavedAddresses>[number]

type CheckoutStepContentProps = {
  viewModel: CheckoutViewModel
  shippingAddresses: CheckoutSavedAddress[]
  billingAddresses: CheckoutSavedAddress[]
  formatCurrency: (value: number) => string
  t: (key: ShopMessageKey) => string
  toLocalizedPath: (path: string) => string
}

export const CheckoutStepContent = ({
  viewModel,
  shippingAddresses,
  billingAddresses,
  formatCurrency,
  t,
  toLocalizedPath,
}: CheckoutStepContentProps) => {
  const subtotal = viewModel.review?.cartSnapshot.subtotal ?? viewModel.session?.cartSnapshot.subtotal ?? 0
  const total = viewModel.review?.cartSnapshot.total ?? viewModel.session?.cartSnapshot.total ?? 0

  if (viewModel.step === 'cart_review') {
    return (
      <CartReviewStep
        items={viewModel.cartItems}
        subtotal={subtotal}
        total={total}
        formatCurrency={formatCurrency}
        disabled={viewModel.isBusy}
        onContinue={viewModel.onContinueFromCartReview}
      />
    )
  }

  if (viewModel.step === 'shipping_address') {
    return (
      <ShippingAddressStep
        form={viewModel.shippingAddressForm}
        validationByField={viewModel.validationByField}
        sameAsShipping={viewModel.sameAsShipping}
        savedAddresses={shippingAddresses}
        disabled={viewModel.isBusy}
        onFieldChange={viewModel.setShippingField}
        onSetForm={viewModel.setShippingForm}
        onSameAsShippingChange={viewModel.setSameAsShipping}
        onSubmit={() => {
          void viewModel.onSubmitShippingAddress()
        }}
        onBack={viewModel.onBackStep}
      />
    )
  }

  if (viewModel.step === 'billing_address') {
    return (
      <BillingAddressStep
        form={viewModel.billingAddressForm}
        validationByField={viewModel.validationByField}
        sameAsShipping={viewModel.sameAsShipping}
        shippingSummary={viewModel.shippingSummary}
        savedAddresses={billingAddresses}
        disabled={viewModel.isBusy}
        onFieldChange={viewModel.setBillingField}
        onSetForm={viewModel.setBillingForm}
        onSameAsShippingChange={viewModel.setSameAsShipping}
        onSubmit={() => {
          void viewModel.onSubmitBillingAddress()
        }}
        onBack={viewModel.onBackStep}
      />
    )
  }

  if (viewModel.step === 'shipping_method') {
    return (
      <ShippingMethodStep
        methods={viewModel.shippingMethods}
        selectedMethod={viewModel.selectedShippingMethod}
        disabled={viewModel.isBusy}
        formatCurrency={formatCurrency}
        onSelectMethod={viewModel.setShippingMethod}
        onSubmit={() => {
          void viewModel.onSubmitShippingMethod()
        }}
        onBack={viewModel.onBackStep}
      />
    )
  }

  if (viewModel.step === 'review') {
    return (
      <ReviewStep
        items={viewModel.cartItems}
        formatCurrency={formatCurrency}
        shippingSummary={viewModel.shippingSummary}
        billingSummary={viewModel.billingSummary}
        shippingMethodSummary={viewModel.shippingMethodSummary}
        paymentProviders={viewModel.paymentProviders}
        selectedPaymentProvider={viewModel.selectedPaymentProvider}
        selectedPaymentProviderRequiresRedirect={viewModel.selectedPaymentProviderRequiresRedirect}
        totalsRows={viewModel.totalsRows}
        disabled={viewModel.isBusy || viewModel.isRedirecting}
        onConfirm={() => {
          void viewModel.onConfirmCheckout()
        }}
        onBack={viewModel.onBackStep}
        onEditShipping={() => viewModel.onNavigateToStep('shipping_address')}
        onEditBilling={() => viewModel.onNavigateToStep('billing_address')}
        onEditShippingMethod={() => viewModel.onNavigateToStep('shipping_method')}
        onSelectPaymentProvider={viewModel.setPaymentProvider}
      />
    )
  }

  if (viewModel.step === 'redirecting_to_payment') {
    return <RedirectingToPaymentStep />
  }

  if (viewModel.step === 'completed') {
    const bankTransferInstructions = viewModel.bankTransferInstructions
    return (
      <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-900 shadow-sm md:p-6">
        <h2 className="text-xl font-semibold">{t('checkout.completed.title')}</h2>
        <p className="mt-2 text-sm">
          {t(
            bankTransferInstructions
              ? 'checkout.completed.bankTransferDescription'
              : 'checkout.completed.description',
          )}
        </p>
        {bankTransferInstructions ? (
          <article className="mt-4 rounded-xl border border-emerald-200 bg-white p-4 text-sm text-slate-900">
            <h3 className="text-sm font-semibold text-slate-900">{t('checkout.completed.bankTransferTitle')}</h3>
            <dl className="mt-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-600">{t('checkout.completed.beneficiary')}</dt>
                <dd className="font-medium">{bankTransferInstructions.beneficiary}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-600">{t('checkout.completed.iban')}</dt>
                <dd className="font-medium">{bankTransferInstructions.iban}</dd>
              </div>
              {bankTransferInstructions.bankName ? (
                <div className="flex items-center justify-between gap-2">
                  <dt className="text-slate-600">{t('checkout.completed.bank')}</dt>
                  <dd className="font-medium">{bankTransferInstructions.bankName}</dd>
                </div>
              ) : null}
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-600">{t('checkout.completed.amount')}</dt>
                <dd className="font-semibold">{formatCurrency(bankTransferInstructions.amount)}</dd>
              </div>
              <div className="flex items-center justify-between gap-2">
                <dt className="text-slate-600">{t('checkout.completed.reference')}</dt>
                <dd className="font-semibold">{bankTransferInstructions.paymentReference}</dd>
              </div>
            </dl>
            {bankTransferInstructions.notes ? (
              <p className="mt-2 text-xs text-slate-600">{bankTransferInstructions.notes}</p>
            ) : null}
          </article>
        ) : null}
        <button
          type="button"
          className="mt-3 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          onClick={() => {
            window.location.assign(toLocalizedPath('/dashboard/orders'))
          }}
        >
          {t('checkout.cta.goToOrders')}
        </button>
      </section>
    )
  }

  return null
}
